import { chakra, keyframes, SpinnerProps } from '@chakra-ui/react'
import { Keyframes } from '@emotion/serialize'
import React from 'react'

import { colors } from '~shared-components/chakra-theme/foundations/colors'
import { useThemeColor } from '~shared-components/hooks/useThemeColor'

import {
  FullScreenLoaderRoundProps,
  FullscreenWrapper,
} from './FullscreenWrapper'

export const FullScreenLoaderRound: React.FC<FullScreenLoaderRoundProps> = ({
  loading,
  opacity = 0.2,
  ...rest
}) => (
  <FullscreenWrapper data-testid="loader" loading={loading} opacity={opacity}>
    <LoaderRound {...rest} size="md" />
  </FullscreenWrapper>
)

export interface LoaderRoundProps extends Omit<SpinnerProps, 'thickness'> {
  thickness?: number
}
const loaderSizes = {
  lg: 96,
  md: 48,
  sm: 24,
  xl: 160,
  xs: 12,
}

const rotation: Keyframes = keyframes`
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
`
const LoaderSvg = chakra('svg')

export const LoaderRound: React.FC<LoaderRoundProps> = ({
  colorScheme,
  size = 'sm',
  thickness: t = 3,
}) => {
  const color80 = useThemeColor(`${colorScheme}.80`)
  const color20 = useThemeColor(`${colorScheme}.20`)

  const s = loaderSizes[size as keyof typeof loaderSizes]
  const colorStart = colorScheme ? color80 : colors.violet[50]
  const colorStop = colorScheme ? color80 : colors.blue[50]
  const colorBack = colorScheme ? color20 : colors.neutral[20]

  return (
    <LoaderSvg
      animation={`${rotation} linear 1s infinite`}
      fill="none"
      height={`${s}px`}
      transformOrigin="center"
      viewBox={`0 0 ${s} ${s}`}
      width={`${s}px`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        height={s - 2 * t}
        rx={s / 2 - t}
        stroke={colorBack}
        strokeWidth={t}
        width={s - 2 * t}
        x={t}
        y={t}
      />
      <path
        d={`M${s / 2} ${t} A${s / 2 - t} ${s / 2 - t} 0 1 1 ${t} ${s / 2}`}
        stroke={colorScheme ? colorStart : 'url(#linear_gradient_loader)'}
        strokeWidth={t}
      />
      {!colorScheme && (
        <defs>
          <linearGradient
            gradientTransform={`rotate(-45 ${s / 2} ${s / 2})`}
            gradientUnits="userSpaceOnUse"
            id="linear_gradient_loader"
          >
            <stop stopColor={colorStart} />
            <stop offset="1" stopColor={colorStop} />
          </linearGradient>
        </defs>
      )}
    </LoaderSvg>
  )
}
